//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Typography
} from '@mui/material';
import {
  Close as CloseIcon, Download, ZoomIn, ZoomOut
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { ArchitectureDiagramWithFileType } from './architectureConfig';

interface ArchitectureDiagramProps {
  open: boolean;
  onClose: () => void;
  diagram: ArchitectureDiagramWithFileType | null;
}

/**
 * Component for displaying architecture diagrams.
 */
const ArchitectureDiagram = (
  { open, onClose, diagram }: ArchitectureDiagramProps
) => {
  const theme = useTheme();
  const [scale, setScale] = useState(1);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) setScale(1);
  }, [open]);

  if (!diagram) return null;

  // Infer file type if not provided.
  const getFileType = (imageUrl: string): string => {
    if (typeof imageUrl === 'string') {
      const lowercaseUrl = imageUrl.toLowerCase();
      if (lowercaseUrl.endsWith('.svg') || lowercaseUrl.includes('svg'))
        return 'svg';
    }
    return 'png'; // default
  };

  const fileType = diagram.fileType || getFileType(diagram.imageUrl);

  const zoomTo = (newScale: number) => {
    const container = containerRef.current;
    if (container) {
      const cx = container.scrollLeft + container.clientWidth / 2;
      const cy = container.scrollTop + container.clientHeight / 2;
      const ratio = newScale / scale;
      setScale(newScale);
      requestAnimationFrame(() => {
        container.scrollLeft = cx * ratio - container.clientWidth / 2;
        container.scrollTop = cy * ratio - container.clientHeight / 2;
      });
    } else {
      setScale(newScale);
    }
  };

  const handleZoomIn = () => {
    zoomTo(Math.min(scale + 0.25, 3));
  };

  const handleZoomOut = () => {
    zoomTo(Math.max(scale - 0.25, 0.25));
  };

  const handleResetZoom = () => {
    setScale(1);
  };

  const handleDownload = () => {
    try {
      // Use the determined file type.
      const fileExtension = fileType;

      // Generate filename.
      const filename =
        `${diagram.title.replace(/\s+/g, '-').toLowerCase()}.${fileExtension}`;

      // Different approach for imported images vs static files.
      if (typeof diagram.imageUrl === 'string' &&
          diagram.imageUrl.startsWith('/')) {
        // For static files in public folder.
        const link = document.createElement('a');
        link.href = diagram.imageUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // For imported images (webpack assets).
        if (fileExtension === 'svg' && typeof diagram.imageUrl === 'string') {
          // For SVG files, fetch the content directly.
          fetch(diagram.imageUrl)
            .then(response => response.text())
            .then(svgContent => {
              const blob = new Blob([svgContent], {type: 'image/svg+xml'});
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = filename;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            });
        } else {
          // For other file types, use canvas approach.
          const image = new Image();
          image.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = image.width;
            canvas.height = image.height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              console.error('Failed to get 2D context for image download.');
              return;
            }
            ctx.drawImage(image, 0, 0);
            const mimeType =
              fileExtension === 'svg' ? 'image/svg+xml' : 'image/png';

            canvas.toBlob((blob) => {
              if (!blob) return;

              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = filename;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            }, mimeType);
          };
          image.src = diagram.imageUrl;
        }
      }
    } catch (error) {
      console.error('Error downloading image:', error);
      alert('There was a problem downloading the image.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            maxHeight: '95vh',
            height: 'auto'
          }
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Typography component="span" variant="h6">{diagram.title}</Typography>
        <Box>
          <IconButton onClick={handleZoomOut} disabled={scale <= 0.25}>
            <ZoomOut />
          </IconButton>
          <IconButton onClick={handleZoomIn} disabled={scale >= 3}>
            <ZoomIn />
          </IconButton>
          <Button onClick={handleResetZoom} size="small">
            Fit
          </Button>
          <IconButton onClick={handleDownload}>
            <Download />
          </IconButton>
          <IconButton edge="end" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ position: 'relative' }}>
        <Box
          ref={containerRef}
          sx={{
            overflow: 'auto',
            display: 'flex',
            width: '100%',
            height: '60vh',
            my: 2
          }}>
          <img
            src={diagram.imageUrl}
            alt={diagram.title}
            style={{
              width: `${scale * 100}%`,
              height: `${scale * 100}%`,
              objectFit: 'contain',
              flexShrink: 0,
              margin: 'auto'
            }}
            onError={(e) => {
              console.warn('Failed to load image:', diagram.imageUrl, e);
            }}
          />
        </Box>
        {diagram.description && (
          <Box sx={{ mt: 2, borderTop: `1px solid ${theme.palette.divider}`, pt: 2 }}>
            <Typography variant="body1">{diagram.description}</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ArchitectureDiagram;
